const splitDateTime = (value) => {
  if (value == null) return "";
  return String(value).trim().split(/\s+/)[0] || "";
};

export const normalizeSearchResultDate = (value) => {
  const raw = splitDateTime(value);
  if (!raw) return null;

  const isoMatch = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }

  const ldsMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (ldsMatch) {
    const [, d, m, y] = ldsMatch;
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }

  return raw;
};

const firstNonEmpty = (...values) => {
  for (const value of values) {
    if (value != null && String(value).trim() !== "") {
      return value;
    }
  }
  return null;
};

const getFlightLegs = (result) => {
  const fullDeal = result?.fullDeal || result || {};
  const linkedFlights =
    result?.linkedFlights ||
    fullDeal?.linkedFlights ||
    fullDeal?.flight ||
    result?.flight ||
    null;

  const firstLink = Array.isArray(linkedFlights) ? linkedFlights[0] : linkedFlights;
  const flights = Array.isArray(firstLink?.flight)
    ? firstLink.flight
    : Array.isArray(firstLink)
      ? firstLink
      : [];

  return {
    outboundLeg: flights[0]?.leg || flights[0] || null,
    returnLeg: flights[1]?.leg || flights[1] || null,
  };
};

export const resolveSearchResultStayDates = (result) => {
  const fullDeal = result?.fullDeal || result || {};
  const hotel = fullDeal?.hotel || result?.hotel || {};
  const { outboundLeg, returnLeg } = getFlightLegs(result);

  const checkIn = firstNonEmpty(
    result?.checkIn,
    result?.checkInDate,
    hotel?.checkInDate,
    hotel?.checkIn,
    result?.depDate,
    result?.dep_date,
    fullDeal?.checkIn,
    fullDeal?.checkInDate,
    fullDeal?.hotel?.checkInDate,
    fullDeal?.hotel?.checkIn,
    fullDeal?.depDate,
    fullDeal?.dep_date,
    outboundLeg?.depDate,
  );
  const checkOut = firstNonEmpty(
    result?.checkOut,
    result?.checkOutDate,
    hotel?.checkOutDate,
    hotel?.checkOut,
    result?.retDate,
    result?.ret_date,
    fullDeal?.checkOut,
    fullDeal?.checkOutDate,
    fullDeal?.hotel?.checkOutDate,
    fullDeal?.hotel?.checkOut,
    fullDeal?.retDate,
    fullDeal?.ret_date,
    returnLeg?.depDate,
  );

  return {
    rawCheckIn: checkIn,
    rawCheckOut: checkOut,
    checkIn: normalizeSearchResultDate(checkIn),
    checkOut: normalizeSearchResultDate(checkOut),
  };
};

/**
 * Category / multi-date package searches stamp the dates that produced the row
 * (`searchDate` or `__searchPayloadDates`). Compare against those instead of the
 * search-form dates, otherwise artist/city filters keep only concert deals from
 * other nights and the date guard wipes the grid.
 */
export const resolveRowStaleGuardExpectedDates = (result, fallbackDates) => {
  const searchDate = result?.searchDate;
  const payload = result?.__searchPayloadDates;
  return {
    checkIn:
      searchDate?.depDate ||
      searchDate?.checkIn ||
      payload?.checkIn ||
      fallbackDates?.checkIn ||
      null,
    checkOut:
      searchDate?.retDate ||
      searchDate?.checkOut ||
      payload?.checkOut ||
      fallbackDates?.checkOut ||
      null,
  };
};

export const isSearchResultStaleForDates = (result, expectedDates) => {
  const expectedCheckIn = normalizeSearchResultDate(expectedDates?.checkIn);
  const expectedCheckOut = normalizeSearchResultDate(expectedDates?.checkOut);
  const resultDates = resolveSearchResultStayDates(result);

  if (!expectedCheckIn || !expectedCheckOut) {
    return { isStaleResult: false, expectedCheckIn, expectedCheckOut, ...resultDates };
  }
  if (!resultDates.checkIn || !resultDates.checkOut) {
    return { isStaleResult: false, expectedCheckIn, expectedCheckOut, ...resultDates };
  }

  return {
    isStaleResult:
      resultDates.checkIn !== expectedCheckIn || resultDates.checkOut !== expectedCheckOut,
    expectedCheckIn,
    expectedCheckOut,
    ...resultDates,
  };
};

export const buildResultCardDatesTrace = ({
  result,
  resultIndex,
  resultSource,
  searchPayloadDates,
  contextDates,
}) => {
  const fullDeal = result?.fullDeal || result || {};
  const hotel = fullDeal?.hotel || result?.hotel || {};
  const dates = resolveSearchResultStayDates(result);

  return {
    hotelName:
      hotel?.hotelName ||
      hotel?.hotelNameEn ||
      result?.hotelName ||
      result?.DisplayName ||
      result?.Name ||
      null,
    cardCheckIn: dates.rawCheckIn,
    cardCheckOut: dates.rawCheckOut,
    packageId: fullDeal?.package?.packageId || fullDeal?.packageId || result?.packageId || null,
    dealId: fullDeal?.dealId || fullDeal?.id || result?.dealId || result?.id || null,
    resultIndex,
    resultSource,
    fullDealDates: {
      depDate: fullDeal?.depDate || fullDeal?.dep_date || null,
      retDate: fullDeal?.retDate || fullDeal?.ret_date || null,
      hotelCheckIn: hotel?.checkInDate || hotel?.checkIn || null,
      hotelCheckOut: hotel?.checkOutDate || hotel?.checkOut || null,
    },
    searchPayloadDates,
    contextDates,
  };
};
